import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PersonIcon from "@mui/icons-material/Person"
import RouteIcon from "@mui/icons-material/AltRoute"
import WarningIcon from "@mui/icons-material/Warning"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import MainLayout from "../../layouts/MainLayout"
import api from "../../services/api"



const DASHBOARD_SURFACE = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
  backgroundColor: "background.paper",
}

const DASHBOARD_FILTER_SURFACE = {
  ...DASHBOARD_SURFACE,
  p: { xs: 1.5, sm: 2 },
  mb: 2.5,
}

const DASHBOARD_SECTION_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
}

const statusLabels = {
  planned: "Planifiée",
  vehicle_out: "Véhicule sorti",
  in_progress: "En cours",
  completed: "Livrée / retour attendu",
  returned: "Retournée",
  cancelled: "Annulée",

  available: "Disponible",
  in_delivery: "En livraison",
  maintenance: "Maintenance",
  inactive: "Inactif",
}


const statusColors = {
  planned: "default",
  vehicle_out: "info",
  in_progress: "warning",
  completed: "success",
  returned: "success",
  cancelled: "error",

  available: "success",
  in_delivery: "info",
  maintenance: "warning",
  inactive: "default",
}


function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}


function formatNumber(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })
}


function formatMoney(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}


function formatCDF(value) {
  return `${formatMoney(value)} FC`
}


function formatUSD(value) {
  return `${formatMoney(value)} $`
}

function formatSignedMoney(value, suffix = "") {
  const number = toNumber(value)
  const sign = number > 0 ? "+" : ""
  return `${sign}${formatMoney(number)}${suffix}`
}

function formatSignedNumber(value, suffix = "") {
  const number = toNumber(value)
  const sign = number > 0 ? "+" : ""
  return `${sign}${formatNumber(number)}${suffix}`
}

function varianceColor(value) {
  const number = toNumber(value)
  if (number > 0) return "error.main"
  if (number < 0) return "success.main"
  return "text.primary"
}

function getEstimatedFuelCostCdf(trip) {
  return toNumber(trip?.estimated_fuel_cost_cdf || trip?.estimated_fuel_cost)
}

function getActualFuelCostCdf(trip) {
  return toNumber(trip?.actual_fuel_cost_cdf || trip?.actual_fuel_cost)
}

function getFuelCostVarianceCdf(trip) {
  if (trip?.fuel_cost_variance_cdf !== undefined && trip?.fuel_cost_variance_cdf !== null) {
    return toNumber(trip.fuel_cost_variance_cdf)
  }

  return getActualFuelCostCdf(trip) - getEstimatedFuelCostCdf(trip)
}

function getFuelCostVarianceUsd(trip) {
  if (trip?.fuel_cost_variance_usd !== undefined && trip?.fuel_cost_variance_usd !== null) {
    return toNumber(trip.fuel_cost_variance_usd)
  }

  return toNumber(trip?.actual_fuel_cost_usd) - toNumber(trip?.estimated_fuel_cost_usd)
}


function formatLiters(value) {
  return `${formatMoney(value)} L`
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return "-"
  }

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}


function getApiErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.message ||
    "Erreur chargement dashboard logistique."
  )
}


function KpiCard({ title, value, subtitle, icon, color = "#2563eb" }) {
  return (
    <Card
      elevation={0}
      sx={{
        ...DASHBOARD_SURFACE,
        height: "100%",
        minHeight: 114,
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.075)",
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              backgroundColor: color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box>
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>

            <Typography variant="h5" fontWeight={900}>
              {value}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


function StatusChip({ status }) {
  return (
    <Chip
      size="small"
      label={statusLabels[status] || status || "-"}
      color={statusColors[status] || "default"}
    />
  )
}


function EmptyRow({ colSpan = 8, message = "Aucune donnée." }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Typography color="text.secondary">{message}</Typography>
      </TableCell>
    </TableRow>
  )
}


function LogisticsDashboardPage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })

  const [dashboard, setDashboard] = useState({
    summary: {
      vehicles: {},
      drivers: {},
      trips: {},
    },
    ongoing_trips: [],
    pending_returns: [],
    vehicles_to_watch: [],
    recent_trips: [],
  })


  async function loadDashboard(customFilters = null) {
    setLoading(true)
    setError("")

    try {
      const params = customFilters || filters

      const response = await api.get("/dashboard-logistics", {
        params,
      })

      setDashboard(response.data || {})
    } catch (err) {
      console.error(err)
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadDashboard({
      start_date: "",
      end_date: "",
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  function updateFilter(field, value) {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }))
  }


  function resetFilters() {
    const emptyFilters = {
      start_date: "",
      end_date: "",
    }

    setFilters(emptyFilters)
    loadDashboard(emptyFilters)
  }


  const vehicles = dashboard?.summary?.vehicles || {}
  const drivers = dashboard?.summary?.drivers || {}
  const trips = dashboard?.summary?.trips || {}

  const ongoingTrips = dashboard?.ongoing_trips || []
  const pendingReturns = dashboard?.pending_returns || []
  const vehiclesToWatch = dashboard?.vehicles_to_watch || []
  const recentTrips = dashboard?.recent_trips || []


  const vehicleStatusChartData = useMemo(() => {
    return [
      {
        name: "Disponibles",
        value: toNumber(
          vehicles.available
        ),
      },
      {
        name: "En livraison",
        value: toNumber(
          vehicles.in_delivery
        ),
      },
      {
        name: "Maintenance",
        value: toNumber(
          vehicles.maintenance
        ),
      },
      {
        name: "Inactifs",
        value: toNumber(
          vehicles.inactive
        ),
      },
    ].filter((item) => item.value > 0)
  }, [vehicles])


  const tripStatusChartData = useMemo(() => {
    const counters = new Map()

    recentTrips.forEach((trip) => {
      const status =
        trip.status || "unknown"

      counters.set(
        status,
        (counters.get(status) || 0) + 1
      )
    })

    return [...counters.entries()].map(
      ([status, value]) => ({
        status,
        name:
          statusLabels[status] ||
          status ||
          "Autre",
        value,
      })
    )
  }, [recentTrips])


  const logisticsVarianceChartData = useMemo(() => {
    return [
      {
        name: "Distance",
        estime: toNumber(
          trips.total_estimated_distance_km
        ),
        reel: toNumber(
          trips.total_actual_distance_km
        ),
      },
      {
        name: "Carburant (L)",
        estime: toNumber(
          trips.total_estimated_fuel_liters
        ),
        reel: toNumber(
          trips.total_actual_fuel_liters
        ),
      },
    ]
  }, [trips])


  const recentTripCostChartData = useMemo(() => {
    return recentTrips
      .slice(0, 6)
      .map((trip) => ({
        name:
          trip.trip_code ||
          `Tournée ${trip.id || ""}`,

        estime:
          getEstimatedFuelCostCdf(
            trip
          ),

        reel:
          getActualFuelCostCdf(
            trip
          ),
      }))
      .filter(
        (item) =>
          item.estime > 0 ||
          item.reel > 0
      )
  }, [recentTrips])


  const chartColors = [
    "#16a34a",
    "#2563eb",
    "#f97316",
    "#7c3aed",
    "#dc2626",
  ]


  return (
    <MainLayout>
      <Box>
        <Box sx={{ mb: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", fontWeight: 900, letterSpacing: 1.1 }}
          >
            PILOTAGE
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-end" }}
            spacing={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={900}
                sx={{ fontSize: { xs: "1.65rem", sm: "1.95rem", md: "2.1rem" } }}
              >
                Dashboard Logistique
              </Typography>

              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.35, maxWidth: 900 }}>
                Supervision des véhicules, chauffeurs, tournées, retours et coûts carburant FC/USD.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadDashboard()}
              disabled={loading}
              sx={{ minHeight: 44, flexShrink: 0 }}
            >
              Actualiser
            </Button>
          </Stack>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Du"
              type="date"
              value={filters.start_date}
              onChange={(event) =>
                updateFilter("start_date", event.target.value)
              }
              slotProps={{
                inputLabel: { shrink: true },
              }}
              fullWidth
            />

            <TextField
              label="Au"
              type="date"
              value={filters.end_date}
              onChange={(event) =>
                updateFilter("end_date", event.target.value)
              }
              slotProps={{
                inputLabel: { shrink: true },
              }}
              fullWidth
            />

            <Button
              variant="contained"
              onClick={() => loadDashboard()}
              disabled={loading}
              sx={{ minWidth: 140 }}
            >
              Charger
            </Button>

            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{ minWidth: 120 }}
            >
              Reset
            </Button>
          </Stack>
        </Paper>

        {loading ? (
          <Paper
            sx={{
              p: 4,
              mb: 2,
              borderRadius: 3,
              textAlign: "center",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>
              Chargement du dashboard logistique...
            </Typography>
          </Paper>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Véhicules disponibles"
            value={vehicles.available || 0}
            subtitle={`${vehicles.total || 0} véhicule(s) au total`}
            icon={<LocalShippingIcon />}
            color="#16a34a"
          />

          <KpiCard
            title="Véhicules en livraison"
            value={vehicles.in_delivery || 0}
            subtitle={`${vehicles.maintenance || 0} en maintenance`}
            icon={<LocalShippingIcon />}
            color="#2563eb"
          />

          <KpiCard
            title="Chauffeurs disponibles"
            value={drivers.available || 0}
            subtitle={`${drivers.total || 0} chauffeur(s) au total`}
            icon={<PersonIcon />}
            color="#0f766e"
          />

          <KpiCard
            title="Chauffeurs en mission"
            value={drivers.on_trip || 0}
            subtitle={`${drivers.absent || 0} absent(s)`}
            icon={<PersonIcon />}
            color="#f97316"
          />

          <KpiCard
            title="Tournées en cours"
            value={trips.active || 0}
            subtitle={`${trips.total || 0} tournée(s) filtrées`}
            icon={<RouteIcon />}
            color="#7c3aed"
          />

          <KpiCard
            title="Retours en attente"
            value={trips.pending_return || 0}
            subtitle="Livraisons terminées sans retour véhicule"
            icon={<WarningIcon />}
            color="#dc2626"
          />

          <KpiCard
            title="Distance estimée"
            value={`${formatNumber(trips.total_estimated_distance_km)} km`}
            subtitle={`Réelle : ${formatNumber(trips.total_actual_distance_km)} km`}
            icon={<RouteIcon />}
            color="#0891b2"
          />

          <KpiCard
            title="Carburant estimé"
            value={formatCDF(
              trips.total_estimated_fuel_cost_cdf ||
                trips.total_estimated_fuel_cost
            )}
            subtitle={`USD : ${formatUSD(trips.total_estimated_fuel_cost_usd)} | Litres : ${formatLiters(trips.total_estimated_fuel_liters)}`}
            icon={<LocalShippingIcon />}
            color="#9333ea"
          />

          <KpiCard
            title="Carburant réel"
            value={formatCDF(
              trips.total_actual_fuel_cost_cdf ||
                trips.total_actual_fuel_cost
            )}
            subtitle={`USD : ${formatUSD(trips.total_actual_fuel_cost_usd)} | Litres : ${formatLiters(trips.total_actual_fuel_liters)}`}
            icon={<LocalShippingIcon />}
            color="#0f766e"
          />

          <KpiCard
            title="Écart logistique"
            value={formatSignedMoney(trips.total_fuel_cost_variance_cdf, " FC")}
            subtitle={`USD : ${formatSignedMoney(trips.total_fuel_cost_variance_usd, " $")} | Distance : ${formatSignedNumber(trips.total_distance_variance_km, " km")}`}
            icon={<WarningIcon />}
            color="#dc2626"
          />
        </Box>

        {/* =========================
            ANALYSE VISUELLE
            ========================= */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              xl: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              État du parc véhicules
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Répartition des véhicules selon leur disponibilité opérationnelle.
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: {
                  xs: 300,
                  sm: 330,
                },
              }}
            >
              {vehicleStatusChartData.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée véhicule disponible.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <PieChart>
                    <Pie
                      data={vehicleStatusChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {vehicleStatusChartData.map(
                        (item, index) => (
                          <Cell
                            key={`${item.name}-${index}`}
                            fill={
                              chartColors[
                                index % chartColors.length
                              ]
                            }
                          />
                        )
                      )}
                    </Pie>

                    <RechartsTooltip
                      formatter={(value) => [
                        formatNumber(value),
                        "Véhicules",
                      ]}
                    />

                    <Legend
                      verticalAlign="bottom"
                      height={44}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>


          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              Estimé vs réel
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Comparaison de la distance parcourue et du carburant consommé.
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: {
                  xs: 300,
                  sm: 330,
                },
              }}
            >
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={logisticsVarianceChartData}
                  margin={{
                    top: 10,
                    right: 15,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    tickLine={false}
                    axisLine={false}
                  />

                  <RechartsTooltip
                    formatter={(value) =>
                      formatNumber(value)
                    }
                  />

                  <Legend />

                  <Bar
                    dataKey="estime"
                    name="Estimé"
                    fill="#2563eb"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={58}
                  />

                  <Bar
                    dataKey="reel"
                    name="Réel"
                    fill="#16a34a"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={58}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>


        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              xl: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              Statut des dernières tournées
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Répartition de l'historique récent selon le statut opérationnel.
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: {
                  xs: 300,
                  sm: 330,
                },
              }}
            >
              {tripStatusChartData.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune tournée récente.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <PieChart>
                    <Pie
                      data={tripStatusChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {tripStatusChartData.map(
                        (item, index) => (
                          <Cell
                            key={`${item.status}-${index}`}
                            fill={
                              chartColors[
                                index % chartColors.length
                              ]
                            }
                          />
                        )
                      )}
                    </Pie>

                    <RechartsTooltip
                      formatter={(value) => [
                        formatNumber(value),
                        "Tournées",
                      ]}
                    />

                    <Legend
                      verticalAlign="bottom"
                      height={44}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>


          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              Coût carburant des dernières tournées
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Comparaison du coût estimé et du coût réellement déclaré en FC.
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: {
                  xs: 300,
                  sm: 330,
                },
              }}
            >
              {recentTripCostChartData.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Aucun coût de tournée disponible.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart
                    data={recentTripCostChartData}
                    margin={{
                      top: 10,
                      right: 15,
                      left: 0,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{
                        fontSize: 11,
                      }}
                    />

                    <YAxis
                      tickLine={false}
                      axisLine={false}
                    />

                    <RechartsTooltip
                      formatter={(value) => [
                        formatCDF(value),
                        "Coût",
                      ]}
                    />

                    <Legend />

                    <Bar
                      dataKey="estime"
                      name="Estimé"
                      fill="#7c3aed"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={46}
                    />

                    <Bar
                      dataKey="reel"
                      name="Réel"
                      fill="#0f766e"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={46}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Box>


        <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, mb: 2, overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={900}>
              Livraisons en cours
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Tournées planifiées, sorties, en cours ou livrées avec retour en attente.
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1250 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Véhicule</TableCell>
                  <TableCell>Chauffeur</TableCell>
                  <TableCell>Entrepôt départ</TableCell>
                  <TableCell>Destinations</TableCell>
                  <TableCell align="right">Arrêts</TableCell>
                  <TableCell align="right">Distance estimée</TableCell>
                  <TableCell align="right">Distance réelle</TableCell>
                  <TableCell align="right">Litres estimés</TableCell>
                  <TableCell align="right">Litres réels</TableCell>
                  <TableCell align="right">Coût estimé FC</TableCell>
                  <TableCell align="right">Coût réel FC</TableCell>
                  <TableCell align="right">Écart FC</TableCell>
                  <TableCell align="right">Coût réel USD</TableCell>
                  <TableCell>Sortie</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ongoingTrips.length === 0 ? (
                  <EmptyRow colSpan={16} message="Aucune livraison en cours." />
                ) : (
                  ongoingTrips.map((trip) => (
                    <TableRow key={trip.id} hover>
                      <TableCell>
                        <Typography fontWeight={800}>
                          {trip.trip_code}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip status={trip.status} />
                      </TableCell>

                      <TableCell>
                        {trip.plate_number || "-"}
                        <Typography variant="caption" display="block">
                          {trip.vehicle_brand || ""} {trip.vehicle_model || ""}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {trip.driver_name || "-"}
                        <Typography variant="caption" display="block">
                          {trip.driver_phone || ""}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {trip.warehouse_name || "-"}
                      </TableCell>

                      <TableCell>
                        {trip.destinations || "-"}
                      </TableCell>

                      <TableCell align="right">
                        {trip.delivered_stops || 0}/{trip.stops_count || 0}
                      </TableCell>

                      <TableCell align="right">
                        {formatNumber(trip.estimated_distance_km)} km
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? `${formatNumber(trip.actual_distance_km)} km` : "-"}
                      </TableCell>

                      <TableCell align="right">
                        {formatLiters(trip.estimated_fuel_liters)}
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatLiters(trip.actual_fuel_liters) : "-"}
                      </TableCell>

                      <TableCell align="right">
                        {formatCDF(getEstimatedFuelCostCdf(trip))}
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatCDF(getActualFuelCostCdf(trip)) : "-"}
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontWeight={800} color={varianceColor(getFuelCostVarianceCdf(trip))}>
                          {trip.has_actual_logistics
                            ? formatSignedMoney(getFuelCostVarianceCdf(trip), " FC")
                            : "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatUSD(trip.actual_fuel_cost_usd) : "-"}
                      </TableCell>

                      <TableCell>
                        {formatDateTime(trip.departure_time)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, overflow: "hidden" }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={900}>
                Retours véhicules en attente
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Livraisons terminées mais véhicule pas encore retourné.
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tournée</TableCell>
                    <TableCell>Véhicule</TableCell>
                    <TableCell>Chauffeur</TableCell>
                    <TableCell align="right">Distance</TableCell>
                    <TableCell align="right">Coût estimé FC</TableCell>
                    <TableCell align="right">Coût réel FC</TableCell>
                    <TableCell align="right">Écart FC</TableCell>
                    <TableCell>Sortie</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pendingReturns.length === 0 ? (
                    <EmptyRow colSpan={8} message="Aucun retour en attente." />
                  ) : (
                    pendingReturns.map((trip) => (
                      <TableRow key={trip.id} hover>
                        <TableCell>
                          <Typography fontWeight={800}>
                            {trip.trip_code}
                          </Typography>
                          <StatusChip status={trip.status} />
                        </TableCell>

                        <TableCell>
                          {trip.plate_number || "-"}
                        </TableCell>

                        <TableCell>
                          {trip.driver_name || "-"}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(trip.estimated_distance_km)} km
                        </TableCell>

                        <TableCell align="right">
                          {formatCDF(getEstimatedFuelCostCdf(trip))}
                        </TableCell>

                        <TableCell align="right">
                          {trip.has_actual_logistics ? formatCDF(getActualFuelCostCdf(trip)) : "-"}
                        </TableCell>

                        <TableCell align="right">
                          <Typography fontWeight={800} color={varianceColor(getFuelCostVarianceCdf(trip))}>
                            {trip.has_actual_logistics
                              ? formatSignedMoney(getFuelCostVarianceCdf(trip), " FC")
                              : "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {formatDateTime(trip.departure_time)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, overflow: "hidden" }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={900}>
                Véhicules à surveiller
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Véhicules en livraison, maintenance ou inactifs.
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plaque</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Véhicule</TableCell>
                    <TableCell>Tournée</TableCell>
                    <TableCell>Chauffeur</TableCell>
                    <TableCell>Sortie</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vehiclesToWatch.length === 0 ? (
                    <EmptyRow colSpan={6} message="Aucun véhicule à surveiller." />
                  ) : (
                    vehiclesToWatch.map((vehicle) => (
                      <TableRow key={vehicle.id} hover>
                        <TableCell>
                          <Typography fontWeight={800}>
                            {vehicle.plate_number}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <StatusChip status={vehicle.status} />
                        </TableCell>

                        <TableCell>
                          {vehicle.brand || "-"} {vehicle.model || ""}
                          <Typography variant="caption" display="block">
                            {vehicle.vehicle_type || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {vehicle.trip_code || "-"}
                        </TableCell>

                        <TableCell>
                          {vehicle.driver_name || "-"}
                        </TableCell>

                        <TableCell>
                          {formatDateTime(vehicle.departure_time)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={900}>
              Dernières tournées
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Historique récent des mouvements logistiques.
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1150 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Véhicule</TableCell>
                  <TableCell>Chauffeur</TableCell>
                  <TableCell>Entrepôt</TableCell>
                  <TableCell align="right">Distance estimée</TableCell>
                  <TableCell align="right">Distance réelle</TableCell>
                  <TableCell align="right">Carburant estimé</TableCell>
                  <TableCell align="right">Carburant réel</TableCell>
                  <TableCell align="right">Coût estimé FC</TableCell>
                  <TableCell align="right">Coût réel FC</TableCell>
                  <TableCell align="right">Écart FC</TableCell>
                  <TableCell align="right">Coût réel USD</TableCell>
                  <TableCell>Sortie</TableCell>
                  <TableCell>Retour</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {recentTrips.length === 0 ? (
                  <EmptyRow colSpan={15} message="Aucune tournée récente." />
                ) : (
                  recentTrips.map((trip) => (
                    <TableRow key={trip.id} hover>
                      <TableCell>
                        <Typography fontWeight={800}>
                          {trip.trip_code}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip status={trip.status} />
                      </TableCell>

                      <TableCell>
                        {trip.plate_number || "-"}
                      </TableCell>

                      <TableCell>
                        {trip.driver_name || "-"}
                      </TableCell>

                      <TableCell>
                        {trip.warehouse_name || "-"}
                      </TableCell>

                      <TableCell align="right">
                        {formatNumber(trip.estimated_distance_km)} km
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? `${formatNumber(trip.actual_distance_km)} km` : "-"}
                      </TableCell>

                      <TableCell align="right">
                        {formatLiters(trip.estimated_fuel_liters)}
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatLiters(trip.actual_fuel_liters) : "-"}
                      </TableCell>

                      <TableCell align="right">
                        {formatCDF(getEstimatedFuelCostCdf(trip))}
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatCDF(getActualFuelCostCdf(trip)) : "-"}
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontWeight={800} color={varianceColor(getFuelCostVarianceCdf(trip))}>
                          {trip.has_actual_logistics
                            ? formatSignedMoney(getFuelCostVarianceCdf(trip), " FC")
                            : "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        {trip.has_actual_logistics ? formatUSD(trip.actual_fuel_cost_usd) : "-"}
                      </TableCell>

                      <TableCell>
                        {formatDateTime(trip.departure_time)}
                      </TableCell>

                      <TableCell>
                        {formatDateTime(trip.return_time)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  )
}


export default LogisticsDashboardPage